"use client";

import { Fragment, useRef, useState, useEffect } from "react";
import { PortableText } from "next-sanity";
import { stegaClean } from "@sanity/client/stega";

import { GroqImage } from "@/helpers/Image";
import JourneyChallengeMedia from "@/components/pageComponents/home/JourneyChallengeMedia";
import JourneyFundingMedia from "@/components/pageComponents/home/JourneyFundingMedia";
import JourneyEliteMedia from "@/components/pageComponents/home/JourneyEliteMedia";
import { PageHomeJourneyStep, PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { GROQButton } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";

const SECTION_BG = "#f0f2f0";

const STEP_COLORS = ["#65896f", "#1AA87F", "#103037"];

// The step button's style is fixed by the design (Figma 9657:19693); the editor only picks the CTA.
const STEP_BUTTON_STYLE: Omit<GROQButton, "cta"> = { type: "default", size: "default", shape: "square" };

function CardText({ step }: { step: PageHomeJourneyStep }) {
  return (
    <div className="flex w-full shrink-0 flex-col justify-center gap-[2.5rem] lg:w-[54.875rem] lg:gap-[4.25rem]">
      {step.name && (
        <h3 className="mb-0 font-geist text-[3.5rem] leading-[1.12] font-medium tracking-[-0.14rem] text-black lg:text-[4rem] lg:tracking-[-0.16rem]">
          {step.name}
        </h3>
      )}

      <div className="flex flex-col gap-[2.5rem] lg:gap-[4rem]">
        {step.content && (
          <div className="richtext richtext-start flex flex-col gap-[2rem] prose-p:mb-0 prose-p:font-geist prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:tracking-[-0.1rem] prose-p:text-black/80 prose-headings:mt-0 prose-headings:mb-0 prose-headings:font-geist-mono prose-headings:text-[1.75rem] prose-headings:leading-[1.5] prose-headings:font-medium prose-headings:text-black/40 prose-headings:uppercase">
            <PortableText
              value={step.content}
              components={richTextComponent}
            />
          </div>
        )}

        <div className="h-px w-full bg-black/10" />

        {(step.topicsLabel || !!step.topics?.length) && (
          <div className="flex flex-col gap-[2rem]">
            {step.topicsLabel && (
              <p className="mb-0 font-geist-mono text-[1.75rem] leading-[1.5] font-medium text-black/40 uppercase">
                {step.topicsLabel}
              </p>
            )}
            <div className="flex flex-col gap-[2rem]">
              {(step.topics ?? []).map((topic, ti) => (
                <div
                  key={`${topic}-${ti}`}
                  className="flex items-center gap-[1.75rem] tracking-[-0.0525rem]">
                  <span className="font-geist-mono text-[1.75rem] leading-none font-medium text-[#228e71]">
                    {String(ti + 1).padStart(2, "0")}
                  </span>
                  <span className="mb-0 font-geist text-[1.75rem] leading-none font-medium text-black">{topic}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hidden until the editor switches it on; with no CTA picked yet it shows "Please select a CTA". */}
      {step.showButton && (
        <CTAButton
          data={{ ...STEP_BUTTON_STYLE, cta: step.cta }}
          data-ui="home"
          className="mt-[1.75rem] w-fit min-w-[25.75rem] self-start lg:mt-0 [&_.button]:min-h-[5.875rem] [&_.button]:text-[1.75rem] [&_svg_path]:fill-white"
        />
      )}
    </div>
  );
}

function CardMedia({ step, index, language }: { step: PageHomeJourneyStep; index: number; language?: string }) {
  // Steps created before the media type existed have no value; the three
  // steps are always challenge → funded → elite, so each gets its composition.
  const fallback = (["challenge", "funding", "elite"] as const)[index] ?? "image";
  const variant = stegaClean(step.mediaVariant) ?? fallback;
  if (variant === "challenge") return <JourneyChallengeMedia step={step} language={language} />;
  if (variant === "funding") return <JourneyFundingMedia step={step} language={language} />;
  if (variant === "elite") return <JourneyEliteMedia step={step} language={language} />;

  return (
    <div className="relative w-full lg:h-[54.125rem] lg:min-h-0 lg:w-[60.977rem] lg:overflow-hidden">
      {step.image && (
        <GroqImage
          src={step.image}
          alt={step.name || ""}
          className="!h-auto !w-full lg:absolute lg:inset-0 lg:!h-full lg:!object-cover"
        />
      )}
    </div>
  );
}

export default function JourneySectionV2({ data }: { data: PageHomeSchema }) {
  const steps = data.journeySteps ?? [];
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [badgeOffsets, setBadgeOffsets] = useState<number[]>([]);

  useEffect(() => {
    const GAP = 8; // px between badges
    const compute = () => {
      if (window.innerWidth < 1024) {
        setBadgeOffsets([]);
        return;
      }
      const widths = badgeRefs.current.map((el) => el?.offsetWidth ?? 0);
      setBadgeOffsets(widths.map((_, i) => widths.slice(0, i).reduce((sum, w) => sum + w, 0) + GAP * i));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [steps]);

  if (!data.journeyTitle && !steps.length) return null;

  return (
    <section className="relative w-full">
      {/* Section header */}
      <div className="mx-auto flex w-[min(86rem,calc(100%-6rem))] flex-col items-center gap-[2rem] pt-[6rem] pb-[6rem] text-center lg:pt-[16rem] lg:pb-[10rem]">
        {data.journeyChip && (
          <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-black italic">{data.journeyChip}</p>
        )}
        {data.journeyTitle && (
          <div className="richtext w-full">
            <PortableText
              value={data.journeyTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {/* Step breadcrumb */}
        {steps.length > 0 && (
          <div className="flex items-center gap-[1rem] pt-[4rem] lg:gap-[4rem]">
            {steps.map((step, i) => {
              const color = STEP_COLORS[i] ?? STEP_COLORS[STEP_COLORS.length - 1];
              return (
                <Fragment key={step._key ?? i}>
                  <div className="flex items-center gap-[0.75rem] lg:gap-[1.625rem]">
                    <div
                      className="relative h-[2.75rem] w-[2.75rem] shrink-0"
                      style={{ backgroundColor: color }}>
                      <span className="absolute top-[0.25rem] left-[0.375rem] font-geist-mono text-[1.625rem] leading-[1.33] font-semibold text-white">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    {step.name && (
                      <span className="font-geist-mono text-[1.25rem] leading-[1.33] font-semibold whitespace-nowrap text-[#555] uppercase lg:text-[1.625rem]">
                        {step.name}
                      </span>
                    )}
                  </div>
                  {i < steps.length - 1 && (
                    <svg
                      viewBox="0 0 6.56066 11.7071"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-[1.375rem] w-[0.75rem] shrink-0">
                      <path
                        opacity="0.1"
                        d="M0.353553 0.353553L5.85355 5.85355L0.353553 11.3536"
                        stroke="#555555"
                      />
                    </svg>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-[8rem] lg:gap-[15.63rem]">
        {steps.map((step, index) => {
          const stepColor = STEP_COLORS[index] ?? STEP_COLORS[STEP_COLORS.length - 1];
          return (
            <div
              key={step._key ?? index}
              className="flex flex-col items-center lg:sticky lg:top-[10rem] lg:w-[157.875rem]">
              {/* Centered wrapper — badge + card share max-width so badge aligns with card edge */}
              <div className="flex w-[min(160rem,calc(100%-4rem))] flex-col lg:w-full">
                {/* Step badge — desktop offset computed dynamically from badge widths */}
                <div
                  ref={(el) => {
                    badgeRefs.current[index] = el;
                  }}
                  className="flex h-[4.125rem] w-fit items-center gap-[1.25rem] pr-[2rem] pl-[1.5rem]"
                  style={{
                    backgroundColor: stepColor,
                    marginLeft: badgeOffsets[index] != null ? `${badgeOffsets[index]}px` : undefined,
                  }}>
                  <div className="flex h-[2.75rem] w-[2.75rem] shrink-0 items-center justify-center bg-[rgba(0,0,0,0.3)]">
                    <span className="font-geist-mono text-[1.625rem] leading-none font-semibold text-white">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="font-geist-mono text-[1.625rem] leading-none font-semibold tracking-[0.05rem] text-white uppercase">
                    {step.name || data.journeyStepLabel || "STEP"}
                  </span>
                </div>

                {/* Card */}
                <div
                  className="w-full overflow-hidden border-t-[0.25rem] bg-[#f5f5f5] px-[3rem] py-[5rem] lg:p-[4rem]"
                  style={{ borderColor: stepColor }}>
                  {/* A shared minimum height keeps the sticky cards fully covered as they stack. */}
                  <div
                    className={cn(
                      "flex w-full flex-col items-center justify-between gap-[5rem] lg:min-h-[57.625rem] lg:flex-row lg:gap-0",
                    )}>
                    <CardText step={step} />
                    <CardMedia
                      step={step}
                      index={index}
                      language={data.settings?.language}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      {data.journeyButton && (
        <div className="flex justify-center px-[2rem] py-[6rem] lg:px-0 lg:py-[10rem]">
          <CTAButton data={data.journeyButton} />
        </div>
      )}
    </section>
  );
}
